import { Component } from '@theme/component';

/**
 * Shoppable video carousel — plays visible videos and reveals cards on scroll.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 *
 * @extends {Component<Refs>}
 */
class WatchAndBuyComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupReveal();
    this.#setupVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#videoObserver?.disconnect();
    this.#revealObserver?.disconnect();
  }

  #setupReveal() {
    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('wab--visible');
            this.#revealObserver?.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    this.#revealObserver.observe(this);
  }

  #setupVideos() {
    const cards = this.querySelectorAll('[data-wab-card]');
    if (!cards.length) return;

    const scroller = this.querySelector('slideshow-slides');

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const media = entry.target.querySelector('deferred-media');
          if (!media) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            this.#playMedia(media);
            entry.target.classList.add('wab-card--playing');
          } else {
            this.#pauseMedia(media);
            entry.target.classList.remove('wab-card--playing');
          }
        });
      },
      { threshold: [0, 0.55, 0.85], root: scroller ?? undefined }
    );

    cards.forEach((card) => {
      this.#videoObserver?.observe(card);

      card.addEventListener('mouseenter', () => {
        const media = card.querySelector('deferred-media');
        if (media) {
          this.#playMedia(media);
          card.classList.add('wab-card--playing');
        }
      });

      card.addEventListener('mouseleave', () => {
        const media = card.querySelector('deferred-media');
        if (!media) return;

        const rect = card.getBoundingClientRect();
        const scrollerRect = scroller?.getBoundingClientRect();
        const isVisible =
          scrollerRect &&
          rect.left >= scrollerRect.left - rect.width * 0.2 &&
          rect.right <= scrollerRect.right + rect.width * 0.2;

        if (!isVisible) {
          this.#pauseMedia(media);
          card.classList.remove('wab-card--playing');
        }
      });
    });
  }

  /**
   * @param {HTMLElement} media
   */
  #playMedia(media) {
    if (media instanceof HTMLElement && 'showDeferredMedia' in media) {
      /** @type {{ showDeferredMedia: () => void }} */ (media).showDeferredMedia();
    }

    const video = media.querySelector('video');
    video?.play().catch(() => {});
  }

  /**
   * @param {HTMLElement} media
   */
  #pauseMedia(media) {
    const video = media.querySelector('video');
    video?.pause();

    const iframe = media.querySelector('iframe[data-video-type]');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    }
  }
}

if (!customElements.get('watch-and-buy-component')) {
  customElements.define('watch-and-buy-component', WatchAndBuyComponent);
}
